import { useState } from 'react';
import type { ReactNode } from 'react';
import { GroupChevron, Pressable, cn } from '@orgus/ui';
import { FACT_CELL, FACT_GRID } from '../sessions/SessionFacts.tsx';

/** One labelled fact: a tiny dim label over its value. A fact without a value does not render. */
export interface Fact {
  label: string;
  value: ReactNode;
  /** The full value, on hover, when the cell truncates it. */
  title?: string;
  /** Ids, paths and branches: mono, one step smaller. */
  mono?: boolean;
}

const COLS: Record<number, string> = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: '' };

/**
 * The detail panels' fact grid, the session drawer's (FACT_GRID): a tiny dim label over each
 * value, cells set apart by thin inset lines. Each inner list is one line of up to four cells;
 * a shorter line spans the width in equal parts. Empty facts drop out, and so does an empty line.
 */
export function FactGrid({ lines, className, label }: { lines: Fact[][]; className?: string; label?: string }) {
  const shown = lines.map((l) => l.filter((f) => f.value != null && f.value !== '' && f.value !== false)).filter((l) => l.length);
  if (!shown.length) return null;
  return (
    <div className={cn('flex min-w-0 flex-col gap-4', className)} role={label ? 'group' : undefined} aria-label={label}>
      {shown.map((line, i) => (
        <dl key={i} className={cn(FACT_GRID, COLS[Math.min(line.length, 4)])}>
          {line.map((f) => (
            <div key={f.label} className={cn(FACT_CELL, 'gap-1')}>
              <dt className="truncate text-caption text-fg-subtle">{f.label}</dt>
              <dd className={cn('flex h-5 min-w-0 items-center gap-1 text-fg-muted num', f.mono ? 'font-mono text-caption' : 'text-ui')} title={f.title}>
                {typeof f.value === 'string' ? <span className="min-w-0 truncate">{f.value}</span> : f.value}
              </dd>
            </div>
          ))}
        </dl>
      ))}
    </div>
  );
}

/**
 * A section of a detail panel: a small title with a quiet aside (a count, a summary). With
 * `collapsible` the title is the disclosure: secondary sections rest folded, their summary in
 * the aside, and open on click.
 */
export function DetailSection({
  title,
  aside,
  collapsible,
  defaultOpen = false,
  actions,
  children,
  className,
}: {
  title: string;
  aside?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Controls at the right end of the title row, while the section is open. */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const shown = !collapsible || open;
  const head = (
    <>
      {collapsible && <GroupChevron open={open} />}
      <h3 className="shrink-0 font-medium text-fg-muted">{title}</h3>
      {aside != null && <span className="flex min-w-0 items-center gap-1 truncate text-fg-subtle num">{aside}</span>}
    </>
  );
  return (
    <section aria-label={title} className={cn('flex min-w-0 flex-col gap-2', className)}>
      <div className="flex h-control-sm min-w-0 items-center gap-2 text-caption">
        {collapsible ? (
          // The chevron hangs in the panel's gutter, so the title lines up with the open sections' titles.
          <Pressable aria-expanded={open} onClick={() => setOpen((v) => !v)} className="-ml-6 flex min-w-0 flex-1 items-center gap-2 rounded-control hover:text-fg">
            {head}
          </Pressable>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">{head}</div>
        )}
        {shown && actions}
      </div>
      {shown && children}
    </section>
  );
}
